import el from "@cypherpotato/el";
import { Button, PrimaryButton } from "../components/button";
import { FormControl, TextInput } from "../components/form";
import { Section } from "../components/layout";
import { cond } from "../util";
import { Icon } from './../components/icon';
import { fuzzyFilter } from "./fuzzy";
import { throttledCall } from "./throttle";
import { MutedText } from "../components/text";

function fancyShowDialog(dialog) {
    dialog.showModal();
    setTimeout(() => {
        dialog.classList.add("visible");
    }, 1);
    dialog.dismiss = fancyCloseDialog.bind(null, dialog);
}

function fancyCloseDialog(dialog) {
    dialog.classList.remove("visible");
    setTimeout(() => {
        dialog.remove();
    }, 250);
}

export function displayAlert({ title, message, type, buttons, dialogSize }) {
    const dialog = el("dialog.dialog-base",
        {
            classList: ["alert-dialog", type, dialogSize]
        },
        el(".dialog-header",
            el("div", title || "Alert"),
            el("button", Icon("close-line"), {
                onClick() {
                    dialog.dismiss();
                }
            })
        ),
        el(".dialog-body",
            cond(
                [type === "alert-warning", Icon("error-warning-line")],
                [type === "alert-info", Icon("information-2-line")],
            ),
            el(".message", message)
        ),
        buttons && el("menu.dialog-buttons",
            buttons.map(
                (args) =>
                    el(
                        "button.app-button",
                        args?.label,
                        {
                            onClick(event) {
                                event.target.closest("dialog").dismiss();
                            }
                        },
                        args
                    ))
        )
    );

    document.body.insertBefore(dialog, document.body.firstChild);
    fancyShowDialog(dialog);
}

export function displayFormControl({ title, message, formControlItem, onConfirm, validate, dialogSize, nullButton, onCancel }) {

    const errorMessages = el.state.create([]);
    let confirmed = false;
    const cancel = () => {
        if (confirmed) return;
        confirmed = true;
        onCancel?.();
        dialog.dismiss();
    };

    const dialog = el("dialog.dialog-base.form-control-dialog",
        {
            classList: [dialogSize]
        },
        el(".dialog-header",
            el("div", title || "Alert"),
            el("button", Icon("close-line"), {
                onClick() {
                    cancel();
                }
            })
        ),
        el(".dialog-body",
            Section(
                el(".message", message),
            ),
            Section(
                FormControl(null,
                    formControlItem,
                    {
                        mount() {
                            formControlItem.focus();
                        }
                    }
                )
            ),
            Section(
                el.state.listening([errorMessages], () =>
                    el(".validation-error",
                        errorMessages.value.length > 0 && el("div", "Please correct the following errors before continuing."),
                        el("ul",
                            errorMessages.value.map(m =>
                                el("li", m)
                            )
                        )
                    )
                )
            )
        ),
        el("menu.dialog-buttons",
            nullButton && Button(
                "Set as empty",
                {
                    async onClick() {
                        await onConfirm(null);
                        dialog.dismiss();
                    },
                    style: {
                        marginRight: "auto"
                    }
                }
            ),
            PrimaryButton(
                "OK",
                {
                    onClick() {
                        const value = formControlItem.value;
                        if (validate) {
                            errorMessages.value = validate.map(([validateFn, errorMessage]) => {
                                if (!validateFn(value)) {
                                    return errorMessage;
                                }
                            }).filter(m => m != null);
                            if (errorMessages.value.length > 0) {
                                return;
                            }
                        }
                        confirmed = true;
                        onConfirm(value);
                        dialog.dismiss();
                    }
                }
            ),
            Button(
                "Cancel",
                {
                    onClick() {
                        cancel();
                    }
                }
            )
        )
    );

    document.body.insertBefore(dialog, document.body.firstChild);
    fancyShowDialog(dialog);
}

export function displayFinder({ placeholder, options, onSelect, onSearch, selectedId }) {

    const MAX_ITEMS = 20;
    const input = el.state.create("");
    const availableOptions = el.state.create(options);
    const initialSelectedIndex = Math.max(0, options.findIndex(option => option.id === selectedId));
    const focusedIndex = el.state.create(initialSelectedIndex);
    let searchVersion = 0;

    function filterOptions() {
        const matches = onSearch
            ? availableOptions.value
            : fuzzyFilter(availableOptions.value, input.value, [
                [option => option.title],
                [option => option.id, 20],
                [option => option.group, 40],
                [option => option.description, 80]
            ]);

        const initialLimit = input.value.length === 0
            ? Math.max(MAX_ITEMS, initialSelectedIndex + 1)
            : MAX_ITEMS;

        return matches.slice(0, initialLimit);
    }

    function selectOption(option) {
        if (!option) {
            return;
        }
        if (onSelect) {
            onSelect(option.id, option);
        }
        dialog.dismiss();
    }

    function focusCurrentOption() {
        requestAnimationFrame(() => {
            const focusedOption = dialog.querySelector(".finder-option.highlighted");
            focusedOption?.focus();
            focusedOption?.scrollIntoView({ block: "nearest" });
        });
    }

    const searchInput = TextInput(null, placeholder, {
        onInput(event) {
            const query = event.target.value;
            const currentSearchVersion = ++searchVersion;
            throttledCall(async () => {
                focusedIndex.value = 0;
                input.value = query;
                if (onSearch) {
                    availableOptions.value = [];
                    const results = await onSearch(query);
                    if (currentSearchVersion === searchVersion) {
                        availableOptions.value = results ?? [];
                    }
                }
            }, 300);
        }
    });

    const dialog = el("dialog.dialog-base.finder-overlay",
        {
            onClick(event) {
                if (event.target === dialog) {
                    dialog.dismiss();
                }
            },
            onKeyDown(event) {
                const visibleOptions = filterOptions();

                if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                    event.preventDefault();
                    if (!visibleOptions.length) {
                        return;
                    }
                    const direction = event.key === "ArrowDown" ? 1 : -1;
                    focusedIndex.value = (focusedIndex.value + direction + visibleOptions.length) % visibleOptions.length;
                    focusCurrentOption();
                    return;
                }

                if (event.key === "Enter") {
                    event.preventDefault();
                    selectOption(visibleOptions[focusedIndex.value]);
                    return;
                }

                if (event.target !== searchInput && !event.ctrlKey && !event.metaKey && !event.altKey && event.key.length === 1) {
                    event.preventDefault();
                    searchInput.focus();
                    searchInput.setRangeText(event.key, searchInput.selectionStart, searchInput.selectionEnd, "end");
                    searchInput.dispatchEvent(new Event("input", { bubbles: true }));
                }
            }
        },
        el(".finder-base",
            searchInput,
            el(".options-container",
                el.state.listening([input, availableOptions, focusedIndex], () => {
                    let currentGroup;
                    const visibleOptions = filterOptions();
                    const activeIndex = Math.min(focusedIndex.value, visibleOptions.length - 1);

                    if (!visibleOptions.length) {
                        if (input.value.trim().length) {
                            return el(".not-found-items-finder-options",
                                MutedText("No results matched your query.")
                            );
                        } else {
                            return el(".empty-finder-options");
                        }
                    }

                    return el(".finder-options",
                        visibleOptions.map((option, optionIndex) => {
                            const group = option.group !== currentGroup && el(".finder-option-group", option.group);
                            currentGroup = option.group;

                            return el(".finder-option-wrapper",
                                group,
                                el("button.finder-option",
                                    option.icon,
                                    el(".finder-option-content",
                                        el(".finder-option-title",
                                            option.title,
                                            option.id === selectedId && el("span.finder-option-selected", "Selected")
                                        ),
                                        option.description && el(".finder-option-description", option.description)
                                    ),
                                    {
                                        onClick() {
                                            selectOption(option);
                                        },
                                        classList: [
                                            option.id === selectedId && "selected",
                                            optionIndex === activeIndex && "highlighted"
                                        ],
                                        ariaSelected: optionIndex === activeIndex
                                    }
                                )
                            );
                        })
                    );
                })
            )
        )
    );

    document.body.insertBefore(dialog, document.body.firstChild);
    fancyShowDialog(dialog);
    focusCurrentOption();
}
